/**
 * PropertyDetailsConfirm renderer · iter303ai-fix9
 *
 * Slim inline confirm-card for Appraisal Booked · Schedule Appraisal · Action 8a.
 * Loads the property by captured_data.property_id and shows an editable-in-place
 * card with core address + property-type + a small set of roof-summary fields.
 *
 * Roof screenshot + roof template are verified in Action 8b (RoofVerify).
 * Tenant + agent info is verified in Action 8c (TenantAgentConfirm — conditional).
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Home as HomeIcon, Save, CheckCircle2, Loader2 } from 'lucide-react';
import { advanceOnEnter } from '../intake/scriptEditControls';

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
});
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const PROPERTY_TYPES = ['Home', 'Rental', 'Business Property', 'Holiday Home'];

// (key, label, kind='text'|'select', options?)
const FIELDS = [
  ['property_address_num',    'Street number',  'text'],
  ['property_address_street', 'Street name',    'text'],
  ['property_address_suburb', 'Suburb',         'text'],
  ['property_address_city',   'City',           'text'],
  ['property_area_code',      'Area / Postcode', 'text'],
  ['property_type',           'Property type',  'select', PROPERTY_TYPES],
  ['preferred_quote_type',    'Primary trade',  'text'],
];

export default function PropertyDetailsConfirm({ action, capturedData, onNext, onBack, onCancel, canBack, runId, continueLabel = 'Confirm & Continue →' }) {
  const propertyId = capturedData?.property_id;
  const [form, setForm] = useState({});
  const [original, setOriginal] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    if (!propertyId) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/properties/${propertyId}`);
      const seed = {};
      for (const [k] of FIELDS) seed[k] = data[k] || '';
      setForm(seed); setOriginal(seed);
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);

  const isDirty = Object.keys(form).some(k => (form[k] || '') !== (original[k] || ''));

  const save = async () => {
    if (!isDirty) return;
    setSaving(true); setErr('');
    try {
      // property_type='Rental' also flips is_rental=True — backend handles that.
      await api.put(`/properties/${propertyId}`, form);
      setOriginal({ ...form });

      // iter303ai-fix9 · CRITICAL — refresh captured_data.property_record on
      // the run so the walker's trigger_when checks (Action 8c tenant/agent
      // conditional) see the fresh property_type value on the next advance.
      // Without this, saving property_type=Rental here leaves the run's
      // captured_data.property_record.property_type stale and Action 8c
      // would incorrectly be skipped.
      if (runId) {
        try {
          const { data: freshProp } = await api.get(`/properties/${propertyId}`);
          await api.patch(`/workflow-runs/${runId}`, {
            captured_data_patch: { property_record: freshProp },
          });
        } catch { /* non-fatal — walker will still use stale record */ }
      }

      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  };

  if (!propertyId) {
    return (
      <div className="p-6 bg-rose-50 border border-rose-200 rounded text-sm text-rose-700">
        No property linked to this run — cannot confirm property details.
        {canBack && <button onClick={onBack} className="ml-3 underline">← Back</button>}
      </div>
    );
  }

  return (
    <div data-testid="wf-property-details-confirm" onKeyDown={advanceOnEnter}>
      <div className="bg-gradient-to-br from-emerald-50 to-white border-l-4 border-emerald-400 rounded-r-lg p-5 mb-5">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-emerald-600 font-semibold mb-1">
          <HomeIcon size={12} /> Confirm Property Details
        </div>
        <div className="text-slate-700 text-sm">
          Verify the property address and type. The type flag routes the next step —
          Rental / Business Property triggers the tenant + agent confirm.
        </div>
      </div>

      {loading ? (
        <div className="p-4 text-sm text-slate-400 italic">Loading property…</div>
      ) : (
        <div className="mb-5 border border-slate-200 rounded-lg bg-white p-4 grid grid-cols-2 gap-3">
          {FIELDS.map(([key, label, kind, opts]) => (
            <label key={key} className="flex flex-col gap-1">
              <span className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold">{label}</span>
              {kind === 'select' ? (
                <select
                  value={form[key] || ''}
                  onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                  data-testid={`wf-property-field-${key}`}
                  className="border border-slate-200 rounded px-2 py-1.5 text-sm bg-white focus:outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-100">
                  <option value="">— pick —</option>
                  {opts.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <input
                  type="text"
                  value={form[key] || ''}
                  onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                  data-testid={`wf-property-field-${key}`}
                  className="border border-slate-200 rounded px-2 py-1.5 text-sm focus:outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-100"
                />
              )}
            </label>
          ))}
        </div>
      )}

      {err && (
        <div className="mb-3 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-3">{err}</div>
      )}

      <div className="flex items-center gap-3">
        {canBack && (
          <button onClick={onBack} data-testid="wf-back-btn"
            className="px-4 py-2 text-slate-500 hover:text-slate-700">← Back</button>
        )}
        {onCancel && (
          <button onClick={onCancel} data-testid="wf-cancel-btn"
            className="px-4 py-2 text-rose-500 hover:text-rose-700 text-sm">Cancel</button>
        )}
        <span className="flex-1" />
        <button
          onClick={save}
          disabled={!isDirty || saving || loading}
          data-testid="wf-property-save-btn"
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-white border-2 border-emerald-500 text-emerald-700 font-semibold rounded hover:bg-emerald-50 disabled:opacity-40 disabled:cursor-not-allowed">
          {saving ? <Loader2 size={14} className="animate-spin" /> : saved ? <CheckCircle2 size={14} className="text-emerald-500" /> : <Save size={14} />}
          {saving ? 'Saving…' : saved ? 'Saved' : 'Save changes'}
        </button>
        <button
          onClick={async () => { if (isDirty) { await save(); } onNext(); }}
          disabled={saving}
          data-testid="wf-property-continue-btn"
          className="px-6 py-2 bg-emerald-600 text-white font-semibold rounded hover:bg-emerald-700 disabled:opacity-40">
          {continueLabel}
        </button>
      </div>
    </div>
  );
}
